import { useCallback, useMemo, useState } from 'react';
import { useMxmCalendar, useMxmPostsCalendar } from '@/hooks';
import { transformCalendarToCalendarData } from '@/features/marketing/utils/dataTransformers';

interface UsePublishingCalendarProps {
  effectiveOrgId?: number;
  effectiveUserId?: string;
  enabled: boolean;
}

/**
 * Hook to manage publishing calendar tab state and operations
 */
export const usePublishingCalendar = ({
  effectiveOrgId,
  effectiveUserId,
  enabled,
}: UsePublishingCalendarProps) => {
  const [currentMonth, setCurrentMonth] = useState<string>(() => {
    const now = new Date();
    const monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    return `${monthNames[now.getMonth()]} ${now.getFullYear()}`;
  });

  // Configuration: Choose data source for calendar
  const USE_POSTS_FOR_CALENDAR = true;

  const isEnabled = enabled && !!(effectiveOrgId || effectiveUserId);

  // Calendar table-based calendar (existing)
  const {
    calendar: calendarEntries,
    loading: calendarTableLoading,
    updatingEntryId: calendarTableUpdatingEntryId,
    error: calendarTableError,
    updateCalendarEntry,
    refetch: refetchCalendarTable,
  } = useMxmCalendar(
    effectiveOrgId,
    currentMonth,
    isEnabled && !USE_POSTS_FOR_CALENDAR,
    effectiveUserId
  );

  // Posts-based calendar (new)
  const {
    calendarData: postsCalendarData,
    loading: postsCalendarLoading,
    error: postsCalendarError,
    refetch: refetchPostsCalendar,
  } = useMxmPostsCalendar(
    effectiveOrgId,
    currentMonth,
    isEnabled && USE_POSTS_FOR_CALENDAR,
    effectiveUserId
  );

  // Choose calendar data source based on configuration
  const calendarData = useMemo(() => {
    if (USE_POSTS_FOR_CALENDAR) {
      // Use posts-based calendar
      return postsCalendarData || {
        monthLabel: currentMonth,
        weeks: [],
      };
    } else {
      // Use calendar table-based calendar (existing)
      if (!calendarEntries.length) {
        return {
          monthLabel: currentMonth,
          weeks: [],
        };
      }
      return transformCalendarToCalendarData(calendarEntries, currentMonth);
    }
  }, [USE_POSTS_FOR_CALENDAR, postsCalendarData, calendarEntries, currentMonth]);

  // Choose loading/error state based on configuration
  const calendarLoading = USE_POSTS_FOR_CALENDAR ? postsCalendarLoading : calendarTableLoading;
  const calendarError = USE_POSTS_FOR_CALENDAR ? postsCalendarError : calendarTableError;
  const refetchCalendar = USE_POSTS_FOR_CALENDAR ? refetchPostsCalendar : refetchCalendarTable;
  const calendarUpdatingEntryId = USE_POSTS_FOR_CALENDAR ? null : calendarTableUpdatingEntryId;

  // Note: handleCalendarUpdate needs updatePost from useSuggestedContent
  // This will be handled in the main component to coordinate between hooks

  const handleMonthChange = useCallback((newMonth: string) => {
    setCurrentMonth(newMonth);
  }, []);

  return {
    currentMonth,
    calendarData,
    calendarLoading,
    calendarError,
    calendarUpdatingEntryId,
    handleMonthChange,
    refetchCalendar,
    updateCalendarEntry, // Export for use in main component
    USE_POSTS_FOR_CALENDAR, // Export for use in main component
  };
};

